import { Fragment, type ComponentChildren } from 'preact'
import { Loader2 } from 'lucide-preact'
import type { SendingDomainPill } from '@/types/sendingDomains'
import { getDisplayErrorMessages } from '@/utils/errorHandling'
import type { CopyRun } from '@/utils/sendingDomainStatus'
import { DomainStateIcon } from './DomainStatePill'

/** A shared sending-domain sentence in these screens' markup; `strongClassName` matches the text it sits in. */
export function CopyRuns({ runs, strongClassName = 'font-medium text-slate-900 dark:text-white' }: { runs: CopyRun[]; strongClassName?: string }) {
  return (
    <>
      {runs.map((run, index) => (
        run.strong ? <strong key={index} className={strongClassName}>{run.text}</strong>
          : run.code ? <code key={index} className="rounded bg-slate-100 px-1 py-0.5 font-mono text-[13px] dark:bg-slate-800">{run.text}</code>
          : <Fragment key={index}>{run.text}</Fragment>
      ))}
    </>
  )
}

type Tone = 'amber' | 'blue' | 'red' | 'green'

const PILL_TONES: Record<SendingDomainPill, Tone> = {
  SettingUp: 'amber',
  Holding: 'amber',
  Warming: 'blue',
  Stopped: 'red',
  Ready: 'green',
}

const TONES: Record<Tone, { box: string; label: string; icon: string }> = {
  amber: {
    box: 'border-amber-200 bg-amber-50 dark:border-amber-800/60 dark:bg-amber-900/15',
    label: 'text-amber-800 dark:text-amber-300',
    icon: 'text-amber-600 dark:text-amber-400',
  },
  blue: {
    box: 'border-blue-200 bg-blue-50 dark:border-blue-800/60 dark:bg-blue-900/15',
    label: 'text-blue-700 dark:text-blue-300',
    icon: 'text-blue-600 dark:text-blue-400',
  },
  red: {
    box: 'border-red-200 bg-red-50 dark:border-red-800/60 dark:bg-red-900/15',
    label: 'text-red-700 dark:text-red-300',
    icon: 'text-red-600 dark:text-red-400',
  },
  green: {
    box: 'border-green-200 bg-green-50 dark:border-green-800/60 dark:bg-green-900/15',
    label: 'text-green-700 dark:text-green-300',
    icon: 'text-green-600 dark:text-green-400',
  },
}

export interface StatusFact {
  label: string
  value: ComponentChildren
}

/** The box that leads a domain's page: what the state means for sending, then the facts behind it. */
export function DomainStatusBox({ pill, verdict, facts }: { pill: SendingDomainPill; verdict: string; facts: StatusFact[] }) {
  const tone = TONES[PILL_TONES[pill]]
  return (
    <section aria-label="Status" className={`rounded-xl border px-5 py-5 sm:px-6 ${tone.box}`}>
      {verdict && (
        <p className="flex items-start gap-2 text-sm font-medium text-slate-900 dark:text-white">
          <DomainStateIcon pill={pill} className={`mt-0.5 h-5 w-5 shrink-0 ${tone.icon}`} />
          <span>{verdict}</span>
        </p>
      )}
      <dl className={`flex flex-wrap gap-x-8 gap-y-4 ${verdict ? 'mt-4 sm:pl-7' : ''}`}>
        {facts.map(fact => (
          <div key={fact.label}>
            <dt className={`text-[11px] font-medium uppercase tracking-wide ${tone.label}`}>{fact.label}</dt>
            <dd className="mt-1 text-sm text-slate-800 dark:text-slate-100">{fact.value}</dd>
          </div>
        ))}
      </dl>
    </section>
  )
}

export function BlockHeading({ id, children }: { id?: string; children: ComponentChildren }) {
  return <h4 id={id} className="text-sm font-semibold text-slate-900 dark:text-white">{children}</h4>
}

/** Stands in for the warm-up figures while they load, or says they could not be. */
export function WarmupLoading({ error }: { error: string | null }) {
  if (error) {
    return (
      <p role="alert" className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
        The warm-up figures could not be loaded. {getDisplayErrorMessages(error).join(' ')}
      </p>
    )
  }
  return (
    <p className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
      <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Loading the warm-up figures…
    </p>
  )
}

const CHIP_TONES = {
  green: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  amber: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  red: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  slate: 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200',
} as const

export function Chip({ tone, children }: { tone: keyof typeof CHIP_TONES; children: ComponentChildren }) {
  return <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-[11px] font-medium ${CHIP_TONES[tone]}`}>{children}</span>
}

/** A labelled line of the "Before X can send" checklist: an icon, a title with its result, then the detail. */
export function CheckItem({ icon, title, chip, children }: {
  icon: ComponentChildren
  title: string
  chip?: ComponentChildren
  children?: ComponentChildren
}) {
  return (
    <div className="grid grid-cols-[20px_minmax(0,1fr)] gap-x-3">
      <span className="pt-px">{icon}</span>
      <div>
        <div className="flex flex-wrap items-center gap-2">
          <h4 className="text-sm font-semibold text-slate-900 dark:text-white">{title}</h4>
          {chip}
        </div>
        {children}
      </div>
    </div>
  )
}
